import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from '@Green-Dot-Corporation/eureka-lib-router-utils';
import {
    initTranslations,
    getTranslation,
} from '@Green-Dot-Corporation/eureka-lib-i18n-utils';
import { LoadingFlow } from '@Green-Dot-Corporation/eureka-ui-loading';
import { resetAuthorization } from '@Green-Dot-Corporation/web-app-utils-api-lib';
import { initializeImages } from '@Green-Dot-Corporation/eureka-ui-media';
import { initMirageServer } from '@Green-Dot-Corporation/eureka-lib-mock-utils';
import { Routes, Route } from 'react-router-dom';
import AuthorizedRoute from './components/security/AuthorizedRoute';
import loadable from '@loadable/component';
import ErrorBoundary from './ErrorBoundary';

// Services
import registerRoutes from './mock/registerRoutes';
import CustomError from './CustomError';
import { getAuthorizationToken } from './services/appService';

// Utils
import { getRoutes, statusRoute } from './services/routing/routes';
import { getRequestParams, setAccessToken } from './utils/serviceUtils';
import { storeApplicationConfiguration } from './utils/applicationConfigurationUtil';

// Constants
import { ROUTE_PATHS, ROUTE_NAMES } from './const/routeNames';
import imageLibrary from './const/imageLibrary';

// Routes

// Mocks

// Styles
import './styles/card-mgmt-module.scss';

// Temporarily added to enable testing
// TODO: Remove this when the APIs are in place in the envs
// Shutdown any existing instance of server to avoid multiple pretender
// instances error
const shouldUseMockData = new URLSearchParams(window?.location?.search).get(
    'shouldUseMockData',
);

if (process.env.NODE_ENV === 'development' || shouldUseMockData === 'true') {
    // istanbul ignore next
    if (window.server && typeof window.server.shutdown === 'function') {
        window.server.shutdown();
    }
    // Create server and pass routes and other options
    // istanbul ignore next
    window.server = initMirageServer({
        routes() {
            registerRoutes(this);
        },
    });
}

const CatchAll = loadable(statusRoute.loader);

class CardMgmtModule extends PureComponent {
    static propTypes = {
        appConfig: PropTypes.object,
        moduleRoutePath: PropTypes.string,
        feature: PropTypes.object,
        location: PropTypes.object,
        navigate: PropTypes.func,
        params: PropTypes.object,
        searchParams: PropTypes.object,
        setSearchParams: PropTypes.func,
        navigateWithinParent: PropTypes.func,
    };

    state = {
        isLoading: true,
    };

    render() {
        const { isLoading, errorUrl } = this.state;
        const { allowed, authenticated } = this.getRoutes();
        const { title, msg, buttonText } = new CustomError().errorObject;
        const errorRouter = {
            router: {
                location: {
                    state: {
                        ctaButtonText: buttonText,
                        title,
                        msg,
                        errorUrl,
                    },
                },
            },
        };

        return (
            <ErrorBoundary>
                <div className={this.baseCls}>
                    <LoadingFlow
                        isLoading={isLoading}
                        {...this.getLoadingFlowLocale()}
                    >
                        <Routes>
                            <Route
                                element={<AuthorizedRoute {...this.props} />}
                            >
                                {this.renderRoutes(authenticated)}
                            </Route>
                            {this.renderRoutes(allowed)}
                            <Route
                                path="*"
                                element={<CatchAll {...errorRouter} />}
                            />
                        </Routes>
                    </LoadingFlow>
                </div>
            </ErrorBoundary>
        );
    }

    componentDidMount() {
        this.initialize();
    }

    renderRoutes = (routes) => {
        const routesArr = Object.getOwnPropertyNames(routes).map(
            (ROUTE_NAME) => {
                const { loader, config } = routes[ROUTE_NAME];
                const Comp = loadable(loader);
                const compProps = this.getRouteCompProps();
                const { isPathExact = true, ...routeConfig } = config ?? {};

                return (
                    <Route
                        path={ROUTE_PATHS[ROUTE_NAME]}
                        key={ROUTE_PATHS[ROUTE_NAME]}
                        exact={isPathExact}
                        element={<Comp {...compProps} {...routeConfig} />}
                    />
                );
            },
        );

        return routesArr;
    };

    getRoutes = () => {
        const sourceRoutes = getRoutes();
        const finalRoutes = Object.getOwnPropertyNames(sourceRoutes).reduce(
            (prev, curr) => {
                const routes = { ...prev };
                const isProtected = sourceRoutes[curr]?.config?.isProtected;

                if (isProtected) {
                    routes.authenticated = {
                        ...prev.authenticated,
                        [curr]: sourceRoutes[curr],
                    };
                } else {
                    routes.allowed = {
                        ...prev.allowed,
                        [curr]: sourceRoutes[curr],
                    };
                }

                return routes;
            },
            { allowed: {}, authenticated: {} },
        );

        return finalRoutes;
    };

    getRouteCompProps = () => {
        const {
            location,
            navigate,
            params,
            searchParams,
            setSearchParams,
            ...rest
        } = this.props;

        const props = {
            ...rest,
            router: {
                location,
                navigate,
                params,
                searchParams,
                setSearchParams,
            },
        };

        return props;
    };

    getLoadingFlowLocale = () => {
        const loadingStateLocale = {
            title: getTranslation('common.loading.titleText'),
        };
        const errorStateLocale = {
            title: getTranslation('common.loading.error.titleText'),
            msg: getTranslation('common.loading.error.msgText'),
        };

        return { loadingStateLocale, errorStateLocale };
    };

    initializeTranslations = async () => {
        const { appConfig } = this.props;
        const { resourcePath, localePath } = appConfig ?? {};

        /* istanbul ignore next */
        if (!this.init) {
            this.init = true;

            await initTranslations({
                defaultTranslations: {
                    'en-US': () => import('./i18n/en-US.json'),
                },
                localizationPath: `${resourcePath}${localePath}`,
            });
        }
    };

    initializeImageLibrary = async () => {
        const { appConfig, feature = {} } = this.props;
        const { resourcePath, branding: { mediaPath } = {} } = appConfig ?? {};

        await initializeImages({
            defaultImages: imageLibrary,
            overridePath: `${resourcePath}${mediaPath}/images/${
                feature.id || 'cardmanagement'
            }`,
        });
    };

    initialize = async () => {
        const { searchParams, appConfig } = this.props;
        const requestParams = getRequestParams(searchParams, appConfig);
        const newState = {
            isLoading: false,
            errorUrl: undefined,
        };

        try {
            await this.initializeTranslations();
            await this.initializeImageLibrary();
            storeApplicationConfiguration(appConfig);
            const { token, errorUrl } = await getAuthorizationToken(
                requestParams,
            );
            newState.errorUrl = errorUrl;
            setAccessToken(token);
            resetAuthorization(token);
        } catch (e) {
            newState.hasError = true;
            const { navigateWithinParent } = this.props;
            navigateWithinParent(ROUTE_PATHS[ROUTE_NAMES.STATUS], {
                state: {
                    ctaButtonText: getTranslation('errorCallout.buttonText'),
                    title: getTranslation('errorCallout.title'),
                    msg: getTranslation('errorCallout.msg'),
                    errorUrl: e.errorDetails?.responseData?.errorUrl,
                },
            });
        } finally {
            this.setState(newState);
        }
    };

    baseCls = 'card-mgmt';
}
export default withRouter(CardMgmtModule);
